import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Select,
  Tag,
  Typography,
  Flex,
  Result,
  Skeleton,
  message,
} from "antd";
import { JobLocation, JobInterface, JobTypeValues } from "api/jobs/dto";
import { useGetJobs } from "api/jobs/useGetJobs";
import React, { useEffect, useState, useRef } from "react";
import { useAddressHelper } from "utils/useAddressHelper";
import SaveIcon from "../../assets/save.svg";
import CompanyIcon from "../../assets/rectangle.svg";
import { calculateDaysAgo, formatDate } from "utils/date";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useGetJobDetails } from "api/jobs/useGetJobDetails";
import ErrorPage from "components/ui/ErrorPage";
import JobApplyForm from "./JobApplyForm";
import "./styles.css";
import {
  QueryParamsStatus,
  useFilterPagination,
} from "hooks/useFilterPagination";
import { isJSONString } from "utils/validate-json";
import { useIsMobile } from "hooks/useIsMobile";
import { InfoCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { storage } from "utils/storage";
import { useGetApplicationStatus } from "api/jobs/useGetApplicationStatus";
import { auth } from "../../firebase/firebase";
import dayjs from "dayjs";

const { Text } = Typography;

const getColorOption = () => {
  const colors = ["gold"];
  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
};

const getFormattedLocations = (jobLocations?: JobLocation[]): string => {
  if (!jobLocations || jobLocations.length === 0) return "Location not available";

  if (jobLocations.length === 1) return jobLocations[0].district;
  if (jobLocations.length === 2) return `${jobLocations[0].district} and ${jobLocations[1].district}`;

  return `${jobLocations[0].district}, ${jobLocations[1].district}... and ${jobLocations.length - 2} more`;
};

const JobDetails = () => {
  const { queryParamaters, onFilterChange } = useFilterPagination();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveJobs, setIsSaveJobs] = useState(false);
  const [visible, setVisible] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState({
    id: "",
    status: "",
  });
  const selectedJobRef = useRef(null);
  const jobDetailsRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const fromHero = new URLSearchParams(location.search).get("from") === "hero";

  const { getProvinceOptions, getDistrictOptions, address, setAddress } =
    useAddressHelper();

  const isMobile = useIsMobile();
  const user = storage.getUserFromStorage();

  const { data, error, isPending } = useGetJobs(queryParamaters);

  if (!id) {
    throw Error("no such jobs on database");
  }

  const { data: applicationData } = useGetApplicationStatus(user?.uid);

  useEffect(() => {
    const currentApplication = applicationData?.find(
      (application) => application.jobId === id
    );
    setApplicationStatus({
      id,
      status: currentApplication?.status || "No Status Available",
    });
  }, [id, applicationData]);

  const {
    data: jobDetails,
    isLoading: isDetailsLoading,
    error: dError,
  } = useGetJobDetails(id);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isMobile && fromHero) {
      setVisible(false);
    }
  }, [isMobile, fromHero]);

  useEffect(() => {
    if (!isPending && !isDetailsLoading && id) {
      if (!isMobile && selectedJobRef.current) {
        selectedJobRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      if (isMobile && !visible && jobDetailsRef.current) {
        jobDetailsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [isPending, isDetailsLoading, id, isMobile, visible]);

  if (error) {
    return <ErrorPage error={error} />;
  }

  const renderJobList = () => {
    if (isPending) {
      return (
        <Card style={{ width: "100%", marginTop: 16, height: "580px" }}>
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} active />
          ))}
        </Card>
      );
    }

    return (
      <div className="__job__list__column">
        {data?.data?.map((i: JobInterface) => (
          <Card
            key={i.id}
            ref={id === i?.id ? selectedJobRef : null}
            id={`job-card-${i.id}`}
            hoverable
            className="mb-8 mr-4"
            onClick={() => {
              navigate(`/enumerator/job/details/${i?.id}`);
              isMobile && setVisible(!visible);
            }}
            style={{
              backgroundColor: `${id === i?.id ? "#EDF6F1" : "white"}`,
              border: `${id === i?.id ? "1px solid #22AD5C" : "none"}`,
            }}
          >
            <Flex justify="space-between" align="start">
              <h3 className="text-2xl md:text-2xl font-medium  text-black  mb-2">
                {i?.jobTitle}
              </h3>
              <img alt="saveIcon" src={SaveIcon} />
            </Flex>

            <Flex gap={"middle"} style={{ margin: "14px 0px" }}>
              <Avatar src={CompanyIcon} size={60} />
              <Flex vertical flex={1}>
                <Flex justify="space-between">
                  <h3 className="text-lg md:text-lg font-semibold  text-black">
                    {i?.companyName}
                  </h3>
                  <div>{calculateDaysAgo(i?.createdAt.toDate())} days ago</div>
                </Flex>
                <Text type="secondary">
                  {getFormattedLocations(i?.joblocation)}
                </Text>
              </Flex>
            </Flex>
            <div className="flex flex-col md:flex-row md:justify-between gap-y-5 px-2 py-2 ml-2">
              <Text className="font-medium text-gray-700">{i?.jobType}</Text>
              <Text className="text-sm text-gray-500">
                Deadline: {formatDate(i?.deadlineAt.toDate())}
              </Text>
            </div>
            <div className="flex flex-col gap-y-5 md:flex-row md:justify-between">
              {i?.salary?.isPubliclyAvailable && i?.salary?.amount && (
                <div className="btn-sm text-neutral-500 bg-slate-50 ml-3 px-2 py-2 rounded-full">
                  Rs {i?.salary?.amount} {i?.salary?.period}
                </div>
              )}
              <Link
                to={`/enumerator/job/details/${i.id}`}
                className="btn-sm text-white bg-primary hover:bg-primary ml-3 px-5 py-2 rounded flex justify-center items-center"
                onClick={() => isMobile && setVisible(!visible)}
              >
                View more
              </Link>
            </div>
            <div className="mt-4">
              {i?.skills?.map((s, index) => (
                <Tag key={s + index} color={getColorOption()}>
                  {s}
                </Tag>
              ))}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const renderJobDetails = () => {
    if (isDetailsLoading) {
      return (
        <Card style={{ width: "100%", marginTop: 16, height: "580px" }}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Card>
      );
    }

    if (dError) {
      return (
        <Result
          status="error"
          title="Server Error"
          subTitle="There seems to be a problem with the server. Please check after a few minutes."
        />
      );
    }

    return (
      <div className="__job__details__column px-5 md:px-0" ref={jobDetailsRef}>
        <Card hoverable className="mb-8">
          <Flex justify="space-between" align="start">
            <h3 className="text-2xl md:text-2xl font-semibold text-black mb-2">
              {jobDetails?.jobTitle}
            </h3>
            <img alt="saveicon" src={SaveIcon} />
          </Flex>

          <div className="mb-4">
            <h3 className="text-lg md:text-lg font-medium text-black">
              {jobDetails?.companyName}
            </h3>
            {/* <Text type="secondary">
              {getFormattedLocations(jobDetails?.districtProvincePairs)}
            </Text> */}
          </div>

          <Flex gap="small" style={{ marginTop: "18px" }}>
          {["SUBMITTED", "SHORT_LISTED", "REJECTED", "PENDING", "APPROVED"].includes(applicationStatus.status) ? (
              <Button
                className="h-10 px-8 border-2 border-border text-base flex items-center gap-2 bg-gray-200 text-gray-500 border-none rounded-full"
                type="primary"
                disabled
              >
                Applied
              </Button>
            ) : (
              <>
                <Button
                  className="h-10 px-8 border-2 border-border text-base flex items-center gap-2 bg-primary text-white border-none rounded-full"
                  type="primary"
                  onClick={() => {
                    if (!auth.currentUser?.emailVerified) {
                      message.error(
                        "User not verified, please check your email to verify."
                      );
                      return;
                    }
                    setIsModalOpen(true);
                    setIsSaveJobs(false);
                  }}
                >
                  Apply
                </Button>
                {applicationStatus.status === "SAVED" ? (
                  <Button
                    className="h-10 px-8 border border-1 border-primary text-primary text-base flex items-center gap-2 rounded-full"
                    disabled
                  >
                    Saved
                  </Button>
                ) : (
                  <Button
                    className="h-10 px-8 border border-1 border-primary text-primary text-base flex items-center gap-2 rounded-full"
                    onClick={() => {
                      setIsModalOpen(true);
                      setIsSaveJobs(true);
                    }}
                  >
                    Save
                  </Button>
                )}
              </>
            )}
          </Flex>
          <hr className="mt-4" />

          {/* About the Job Section */}
          <div className="mt-3">
            <Text strong className="text-lg mb-2">About the job</Text>
            <br />
            <Text type="secondary" className="leading-7 text-lg mt-2">
              {jobDetails?.jobDescription}
            </Text>
          </div>

          <hr className="my-3" />
          <div>
            <Text strong className="text-lg mb-2">Job Type :</Text>
            <Text type="secondary" className="mt-2 text-lg leading-7 ms-3">
              {jobDetails?.jobType}
            </Text>
          </div>

          <hr className="my-2" />
          <div>
            <Text strong className="text-lg mb-2">Job Posted :</Text>
            <Text type="secondary" className="leading-7 text-lg mt-2 ms-3">
              {formatDate(dayjs(jobDetails?.createdAt.toDate()))}
            </Text>
          </div>

          <hr className="my-2" />
          <div>
            <Text strong className="text-lg mb-2 block">Vacancy Details:</Text>
            <div
              style={{
                backgroundColor: "#F1F3F4",
                border: "1px solid #ddd",
                borderRadius: 4,
                padding: "12px",
                marginBottom: "16px",
                marginTop: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                <Text strong>Note:</Text>
              </div>

              <Row gutter={12} align="top">
                <Col flex="auto">
                  <Text strong>No. of Vacancies:</Text>
                  <Text type="secondary" className="ms-2">
                    {jobDetails?.numberOfVacancy || "-"}
                  </Text>
                </Col>
                <Col flex="auto">
                  <Text strong>Skills:</Text>
                  <Text type="secondary" className="ms-2">
                    {jobDetails?.skills?.length ? jobDetails?.skills.join(", ") : "Not Available"}
                  </Text>
                </Col>
              </Row>

              <Row gutter={12} align="top" style={{ marginTop: 8 }}>
                <Col span={24}>
                  <Text strong>Locations:</Text>
                  <Text type="secondary" className="ms-2">
                    {jobDetails?.joblocation?.map(loc => {
                      return loc.languages && loc.languages.length > 0
                        ? `${loc.district} (${loc.languages.join(", ")})`
                        : loc.district;
                    }).join(", ") || "N/A"}

                  </Text>
                </Col>
              </Row>
            </div>
          </div>

          {jobDetails?.salary?.isPubliclyAvailable && (
            <>
              <hr className="my-2" />
              <div>
                <Text strong className="text-lg mb-2">Salary :</Text>
                <Text
                  type="secondary"
                  className="leading-7 text-lg mt-2 ms-3"
                >
                  Rs {jobDetails?.salary?.amount} {jobDetails?.salary?.period}
                </Text>
              </div>
            </>
          )}

          <hr className="my-2" />
          <div>
            <Text strong className="text-lg mb-2">Experience :</Text>
            <Text type="secondary" className="leading-7 text-lg mt-2 ms-3">
              {jobDetails?.experience}
            </Text>
          </div>

          <hr className="my-2" />
          <div>
            <Text strong className="text-lg mb-2">DeadLine :</Text>
            <Text type="secondary" className="leading-7 text-lg mt-2 ms-3">
              {formatDate(jobDetails?.deadlineAt)}
            </Text>
          </div>
        </Card>
      </div>
    );
  };

  const renderLargeDeviceContent = () => {
    return (
      <>
        <div className="flex flex-col md:flex-row md:justify-between">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              allowClear
              style={{ border: "none", width: "100%" }}
              placeholder="Select Province"
              className="__job__type"
              options={getProvinceOptions()}
              onChange={(value) => {
                onFilterChange(value, QueryParamsStatus.FIND_BY_PROVINCE);
                setAddress({ ...address, province: value });
              }}
            />
            <Select
              allowClear
              style={{ border: "none", width: "100%" }}
              placeholder="Select District"
              className="__job__type"
              options={getDistrictOptions()}
              onChange={(value) => {
                onFilterChange(value, QueryParamsStatus.FIND_BY_DISTRICT);
                setAddress({ ...address, district: value });
              }}
            />
          </div>
          <div>
            <Select
              value={queryParamaters?.findByJobType}
              allowClear
              placeholder="Sort by Job Type"
              className="__job__type"
              options={[
                { value: JobTypeValues.FULL_TIME, label: "Full Time" },
                { value: JobTypeValues.PART_TIME, label: "Part Time" },
                { value: JobTypeValues.CONTRACT, label: "Contract" },
                { value: JobTypeValues.INTERNSHIP, label: "Internship" },
              ]}
              onChange={(value) =>
                onFilterChange(value, QueryParamsStatus.FIND_BY_JOB_TYPE)
              }
            />
          </div>
        </div>
        <Divider />
        <Row gutter={48}>
          <Col span={12}>{renderJobList()}</Col>
          <Col span={12}>{renderJobDetails()}</Col>
        </Row>
      </>
    );
  };

  const renderMobileDeviceContent = () => {
    return (
      <>
        {visible && (
          <div className="px-5">
            <div className="flex flex-col gap-y-7">
              <Select
                allowClear
                style={{ border: "none", width: "100%" }}
                placeholder="Select Province"
                className="__job__type"
                options={getProvinceOptions()}
                onChange={(value) => {
                  const province =
                    value && isJSONString(value)
                      ? JSON.parse(value)?.name
                      : value;
                  onFilterChange(province, QueryParamsStatus.FIND_BY_PROVINCE);
                  setAddress({ ...address, province: value });
                }}
              />
              <Select
                allowClear
                style={{ border: "none", width: "100%" }}
                placeholder="Select District"
                className="__job__type"
                options={getDistrictOptions()}
                onChange={(value) => {
                  onFilterChange(value, QueryParamsStatus.FIND_BY_DISTRICT);
                  setAddress({ ...address, district: value });
                }}
              />

              <Select
                value={queryParamaters?.findByJobType}
                allowClear
                placeholder="Sort by Job Type"
                className="__job__type"
                options={[
                  { value: JobTypeValues.FULL_TIME, label: "Full Time" },
                  { value: JobTypeValues.PART_TIME, label: "Part Time" },
                  { value: JobTypeValues.CONTRACT, label: "Contract" },
                  { value: JobTypeValues.INTERNSHIP, label: "Internship" },
                ]}
                onChange={(value) =>
                  onFilterChange(value, QueryParamsStatus.FIND_BY_JOB_TYPE)
                }
              />
            </div>

            <Divider />
            <Row>
              <Col span={24}>{renderJobList()}</Col>
            </Row>
          </div>
        )}
        {!visible && (
          <div
            className="flex gap-x-2 mb-2 pl-5"
            onClick={() => setVisible(!visible)}
          >
            <LeftOutlined /> Back
          </div>
        )}
        {!visible && renderJobDetails()}
      </>
    );
  };

  return (
    <>
      <div className="max-w-6xl mx-auto my-12 __job__list_container">
        {isMobile ? renderMobileDeviceContent() : renderLargeDeviceContent()}
      </div>

      <JobApplyForm
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        id={id}
        isSaveJobs={isSaveJobs}
        enumeratorId={user.uid}
        onSaveSuccess={() => window.location.reload()} // Refresh on save success
      />
    </>
  );
};

export default JobDetails;
